import { createContext, useContext, useEffect, useState } from "react";
import supabase from "../utils/supabase";

const ChatContext = createContext();

export function ChatProvider({ children }) {
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (!currentUser?.id) return;

    const channel = supabase.channel("chat-updates");

    const subscription = channel
      .on(
        "postgres_changes",
        {
          event: "*", // Listen to all events
          schema: "public",
          table: "messages",
        },
        async (payload) => {
          // For new messages
          if (payload.eventType === "INSERT") {
            // If we're in the chat where the message was sent
            if (currentChat?.id === payload.new.chat_id) {
              // Fetch the latest messages to ensure correct order
              const messages = await loadMessages(payload.new.chat_id);
              setCurrentChat((prev) => ({
                ...prev,
                messages,
              }));
            }
            // Update chat list for all users
            if (currentUser?.id) {
              loadUserChats(currentUser.id);
            }
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [currentChat?.id, currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id) return;

    // Update user's online status
    const updateStatus = async () => {
      await supabase.from("user_status").upsert(
        {
          user_id: currentUser.id,
          is_online: true,
          last_seen: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );
    };

    updateStatus();

    // Update last_seen periodically while online
    const interval = setInterval(updateStatus, 30000); // every 30 seconds

    // Handle page visibility change
    const handleVisibilityChange = () => {
      updateStatus();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Handle user going offline
    const handleOffline = async () => {
      await supabase.from("user_status").upsert(
        {
          user_id: currentUser.id,
          is_online: false,
          last_seen: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );
    };

    window.addEventListener("beforeunload", handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      handleOffline();
    };
  }, [currentUser?.id]);

  const loadUserChats = async (userId) => {
    const { data, error } = await supabase
      .from("chats")
      .select(
        `
        *,
        user1_info:public_profiles!chats_user1_fkey(id, name, user_type, role),
        user2_info:public_profiles!chats_user2_fkey(id, name, user_type, role)
      `
      )
      .or(`user1.eq.${userId},user2.eq.${userId}`);

    if (error) throw error;
    setChats(data);
  };

  const loadMessages = async (chatId) => {
    const { data: messages, error } = await supabase
      .from("messages")
      .select("*")
      .eq("chat_id", chatId)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return messages;
  };

  const sendMessage = async (chatId, senderId, receiverId, message) => {
    const { data, error } = await supabase.from("messages").insert({
      chat_id: chatId,
      sender_id: senderId,
      receiver_id: receiverId,
      message,
    });

    if (error) throw error;
    return data;
  };

  const createChat = async (user1, user2) => {
    // First check if chat exists
    const { data: existingChat } = await supabase
      .from("chats")
      .select("*")
      .or(
        `and(user1.eq.${user1},user2.eq.${user2}),and(user1.eq.${user2},user2.eq.${user1})`
      )
      .single();

    if (existingChat) {
      return existingChat;
    }

    // If no existing chat, create new one
    const { data, error } = await supabase
      .from("chats")
      .insert({ user1, user2 })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const updateUserStatus = async (userId, isOnline) => {
    const { error } = await supabase
      .from("user_status")
      .upsert({
        user_id: userId,
        is_online: isOnline,
        last_seen: new Date().toISOString(),
      })
      .select();

    if (error) console.error("Error updating user status:", error);
  };

  const markMessagesAsRead = async (chatId, userId) => {
    const { error } = await supabase
      .from("messages")
      .update({ is_read: true })
      .eq("chat_id", chatId)
      .eq("receiver_id", userId)
      .eq("is_read", false);

    if (error) console.error("Error marking messages as read:", error);
  };

  const getUserStatus = async (userId) => {
    const { data, error } = await supabase
      .from("user_status")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error) console.error("Error fetching user status:", error);
    return data;
  };

  return (
    <ChatContext.Provider
      value={{
        chats,
        currentChat,
        currentUser,
        setCurrentUser,
        setCurrentChat,
        loadUserChats,
        loadMessages,
        sendMessage,
        createChat,
        markMessagesAsRead,
        getUserStatus,
        updateUserStatus,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
